import { getIn } from 'formik';
import Stack from '@mui/material/Stack';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import SelectField from '@shared/components/inputs/SelectField/SelectField';
import type { SelectOption } from '@shared/components/inputs/SelectField/SelectField.types';
import type { FormikSelectFieldProps } from './FormikSelectField.types';

export const FormikSelectField = <Values,>({
  formik,
  name,
  label,
  optional,
  options,
  placeholder,
  storeOption,
  mapOption,
  onValueChange,
  containerSx,
}: FormikSelectFieldProps<Values>): React.JSX.Element => {
  const storedValue: unknown = getIn(formik.values, name);
  const selectedOption = storeOption
    ? ((storedValue as SelectOption | null) ?? null)
    : (options.find((option) => option.value === storedValue) ?? null);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = getIn(formik.errors, name) as string | undefined;

  const handleChange = (option: SelectOption | null) => {
    if (storeOption) {
      formik.setFieldValue(name, mapOption ? mapOption(option) : option);
      onValueChange?.(option?.value ?? '');
      return;
    }
    const nextValue = option?.value ?? '';
    formik.setFieldValue(name, nextValue);
    onValueChange?.(nextValue);
  };

  return (
    <Stack gap="10px" sx={containerSx}>
      <TextFieldLabel label={label} optional={optional} />
      <SelectField
        placeholder={placeholder}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        onBlur={() => formik.setFieldTouched(name, true)}
        error={isTouched && !!errorMessage}
        helperText={isTouched && errorMessage}
      />
    </Stack>
  );
};
